import {
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  ParagraphNode,
  SerializedParagraphNode
} from 'lexical';
import { v4 as uuidv4 } from 'uuid';
import { createCommentableNode } from '../CommentableMixin';
import React from 'react';

// import { ParagraphInteractionBar } from './ParagraphInteractionBar';
import ReactionArticleIcon from '../../../../assets/reaction-article-icon.svg';

import './styles.css';

export type SerializedCommentableParagraphNode = SerializedParagraphNode & {
  type: 'commentable-paragraph';
  version: number;
  nodeId: string;
  commentCount: number;
  reactionCount: number;
  nodeData: {
    reactionCount: number;
    isReacted: boolean;
    isCommented: boolean;
    reactedType?: string;
    comment?: {
      isCommented: boolean;
      commentCount: number;
    };
  };
};

const CommentableParagraphNodeBase = createCommentableNode(ParagraphNode);

export class CommentableParagraphNode extends CommentableParagraphNodeBase {
  __nodeId: string;
  __commentCount: number;
  __reactionCount: number;
  __nodeData: any = {};
  __version: number = 0; // Add version tracking

  static getType(): string {
    return 'commentable-paragraph';
  }

  static clone(node: CommentableParagraphNode): CommentableParagraphNode {
    return new CommentableParagraphNode(
      node.__key,
      node.__nodeId,
      node.__commentCount,
      node.__reactionCount,
      node.__nodeData,
      node.__version
    );
  }

  constructor(
    key?: NodeKey,
    nodeId?: string,
    commentCount: number = 0,
    reactionCount: number = 0,
    nodeData: any = {},
    version: number = 0
  ) {
    super(key);
    this.__nodeId = nodeId || uuidv4();
    this.__commentCount = commentCount;
    this.__reactionCount = reactionCount;
    this.__nodeData = { ...nodeData }; // Ensure it's a new object reference
    this.__version = version;
  }

  get __paragraphId(): string {
    return this.__nodeId;
  }

  set __paragraphId(id: string) {
    this.__nodeId = id;
  }

  getParagraph(): any {
    return this.getNodeData();
  }

  setParagraph(paragraph: any): void {
    this.setNodeData(paragraph);
  }

  // Override updateDOM - since Lexical's prevNode is unreliable, always check for recent updates
  updateDOM(prevNode: CommentableParagraphNode, dom: HTMLElement, _config: EditorConfig): boolean {
    const currentNodeData = this.getNodeData();

    // Check if this update happened recently (within last 2 seconds)
    const now = Date.now();
    const lastUpdated = currentNodeData?.timestamp || 0;
    const isRecentUpdate = now - lastUpdated < 2000;

    if (isRecentUpdate) {
      return true; // Force complete recreation
    }

    // For older nodes, try to do incremental updates
    const _baseInstance = this as any;

    // Find the interaction elements
    const reactionSummary = dom.querySelector('.reaction-summary') as HTMLElement;
    const userReactionButton = dom.querySelector('.user-reaction-button') as HTMLElement;
    const commentActionButton = dom.querySelector('.comment-action-button') as HTMLElement;
    const interactionContainer = dom.querySelector('.interaction-container') as HTMLElement;

    if (!reactionSummary || !userReactionButton || !commentActionButton || !interactionContainer) {
      return true;
    }

    // If elements exist but no recent update, assume no changes needed
    return false;
  }

  exportJSON(): SerializedCommentableParagraphNode {
    const baseJSON = super.exportJSON() as SerializedParagraphNode;
    return {
      ...baseJSON,
      type: 'commentable-paragraph',
      version: this.__version || 1,
      nodeId: this.__nodeId,
      commentCount: this.__commentCount,
      reactionCount: this.__reactionCount,
      nodeData: this.__nodeData
    };
  }

  static importJSON(serializedNode: SerializedCommentableParagraphNode): CommentableParagraphNode {
    const node = new CommentableParagraphNode(
      undefined,
      serializedNode.nodeId,
      serializedNode.commentCount,
      serializedNode.reactionCount,
      serializedNode.nodeData,
      serializedNode.version || 1
    );

    node.setFormat(serializedNode.format);
    node.setIndent(serializedNode.indent);
    node.setDirection(serializedNode.direction);

    return node;
  }
}

export function $createCommentableParagraphNode(
  commentCount: number = 0,
  nodeId?: string
): CommentableParagraphNode {
  return new CommentableParagraphNode(undefined, nodeId, commentCount, 0, {});
}

export function $isCommentableParagraphNode(
  node: LexicalNode | null | undefined
): node is CommentableParagraphNode {
  return node instanceof CommentableParagraphNode;
}
